import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import Navigation from '../components/Navigation';
import BottomTabBar from '../components/BottomTabBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Clock,
  Bug,
  Lightbulb,
  HelpCircle,
  AlertTriangle,
  FileText,
  CheckCircle2,
  Loader,
  Bell,
  ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MyFeedbackPage = () => {
  const navigate = useNavigate();
  const { feedbackId } = useParams();
  const { user } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchFeedbackList = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackList(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load your feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeedbackDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedFeedback(response.data);
      
      // Mark as seen if there are updates
      if (hasUpdates(response.data)) {
        await axios.patch(`${API}/api/feedback/${id}/seen`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update the list to reflect the seen status
        setFeedbackList(prev => prev.map(f => 
          f.id === id ? { ...f, user_seen_at: new Date().toISOString() } : f
        ));
      }
    } catch (error) {
      console.error('Error fetching feedback detail:', error);
      toast.error('Failed to load feedback details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchFeedbackList();
    }
  }, [user, fetchFeedbackList]);

  useEffect(() => {
    if (feedbackId) {
      fetchFeedbackDetail(feedbackId);
    } else {
      setSelectedFeedback(null);
    }
  }, [feedbackId, fetchFeedbackDetail]);

  // Check if feedback has unseen updates
  const hasUpdates = (feedback) => {
    if (!feedback.updated_at) return false;
    if (!feedback.user_seen_at) return true;
    return new Date(feedback.updated_at) > new Date(feedback.user_seen_at);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'bug':
        return <Bug className="h-4 w-4 text-red-500" />;
      case 'feature_request':
        return <Lightbulb className="h-4 w-4 text-amber-500" />;
      case 'confusing':
        return <HelpCircle className="h-4 w-4 text-blue-500" />;
      case 'report':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'bug':
        return 'Bug Report';
      case 'feature_request':
        return 'Feature Request';
      case 'confusing':
        return 'Confusing Experience';
      case 'report':
        return 'Report Issue';
      default:
        return 'General Feedback';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge className="bg-blue-100 text-blue-700">Submitted</Badge>;
      case 'in_review':
        return <Badge className="bg-amber-100 text-amber-700">In Review</Badge>;
      case 'resolved':
        return <Badge className="bg-emerald-100 text-emerald-700">Resolved</Badge>;
      case 'closed':
        return <Badge className="bg-gray-100 text-gray-600">Closed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600">{status}</Badge>;
    }
  };

  const handleFeedbackClick = (feedback) => {
    navigate(`/my-feedback/${feedback.id}`);
  };

  const handleBack = () => {
    navigate('/my-feedback');
  };

  // Detail View
  if (feedbackId && selectedFeedback) {
    return (
      <div className="min-h-screen-safe bg-gray-50 pb-20 md:pb-0 flex flex-col">
        <Navigation showFeedback={false} hideMobileNav={true} />
        
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-8 flex-1 w-full">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to My Feedback
          </Button>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {getCategoryIcon(selectedFeedback.category)}
                      <span className="text-sm text-gray-500">
                        {getCategoryLabel(selectedFeedback.category)}
                      </span>
                    </div>
                    <CardTitle className="text-xl">{selectedFeedback.subject}</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-2">
                      <Clock className="h-3 w-3" />
                      Submitted {formatDateTime(selectedFeedback.created_at)}
                    </CardDescription>
                  </div>
                  {getStatusBadge(selectedFeedback.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Original Message */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Your Message</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedFeedback.description}</p>
                  </div>
                </div>

                {/* Admin Response */}
                {selectedFeedback.admin_comment && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Response from Support
                    </h3>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedFeedback.admin_comment}</p>
                      {selectedFeedback.resolved_at && (
                        <p className="text-xs text-gray-500 mt-3">
                          {formatDateTime(selectedFeedback.resolved_at)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Status Timeline */}
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Status</h3>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedFeedback.status)}
                    {selectedFeedback.status === 'new' && (
                      <span className="text-sm text-gray-500">
                        We&apos;ve received your feedback and will review it soon.
                      </span>
                    )}
                    {selectedFeedback.status === 'in_review' && (
                      <span className="text-sm text-gray-500">
                        Our team is currently looking into this.
                      </span>
                    )}
                    {selectedFeedback.status === 'resolved' && (
                      <span className="text-sm text-gray-500">
                        This feedback has been addressed.
                      </span>
                    )}
                    {selectedFeedback.status === 'closed' && (
                      <span className="text-sm text-gray-500">
                        This feedback has been closed.
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <BottomTabBar />
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen-safe bg-gray-50 pb-20 md:pb-0 flex flex-col">
      <Navigation showFeedback={true} hideMobileNav={true} />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-8 flex-1 w-full">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-600" />
              My Feedback
            </CardTitle>
            <CardDescription>
              Track the status of feedback you&apos;ve submitted
            </CardDescription>
          </CardHeader>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : feedbackList.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium text-gray-700">No feedback submitted yet</p>
              <p className="text-sm text-gray-500 mt-1">
                When you submit feedback, you&apos;ll be able to track it here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {feedbackList.map((feedback) => {
              const showUpdateIndicator = hasUpdates(feedback);
              
              return (
                <Card
                  key={feedback.id}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    showUpdateIndicator ? 'ring-2 ring-emerald-300 ring-offset-1' : ''
                  }`}
                  onClick={() => handleFeedbackClick(feedback)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getCategoryIcon(feedback.category)}
                          <span className="text-xs text-gray-500">
                            {getCategoryLabel(feedback.category)}
                          </span>
                          {showUpdateIndicator && (
                            <Badge className="bg-emerald-500 text-white text-xs px-1.5">
                              <Bell className="h-3 w-3 mr-1" />
                              Updated
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-medium text-gray-900 line-clamp-1">
                          {feedback.subject}
                        </h3>
                        <p className="text-sm text-gray-500 line-clamp-1 mt-1">
                          {feedback.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <Clock className="h-3 w-3" />
                          <span>{formatDate(feedback.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getStatusBadge(feedback.status)}
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <BottomTabBar />
    </div>
  );
};

export default MyFeedbackPage;
